import { createTypedHubConnection } from '../realtime/hubFactory';
import type { TypedHubConnection } from '../realtime/hubFactory';

export type LogoutHubClientEvents = {
  ForceLogout: () => void;
};

export type LogoutHubConnection = TypedHubConnection<LogoutHubClientEvents>;

export type ForceLogoutWatcherOptions = {
  emsServerUrl: string;
  accessTokenFactory: () => Promise<string>;
  onForceLogout: () => void;
  onError?: (message: string, error: unknown) => void;
};

export type ForceLogoutWatcher = {
  start(): void;
  dispose(): void;
};

export const FORCE_LOGOUT_FIRST_RETRY_DELAY_MS = 1_000;
export const FORCE_LOGOUT_MAX_RETRY_DELAY_MS = 30_000;

export function createForceLogoutWatcher(options: ForceLogoutWatcherOptions): ForceLogoutWatcher {
  const reportError = options.onError ?? (() => undefined);
  const hub = createTypedHubConnection<LogoutHubClientEvents>({
    url: `${options.emsServerUrl.replace(/\/+$/, '')}/hub/logout`,
    hubName: 'LogoutHubConnection',
    accessTokenFactory: options.accessTokenFactory,
  });

  const detachForceLogout = hub.on('ForceLogout', () => options.onForceLogout());

  let disposed = false;
  let started = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  function retryDelayMs(attempt: number): number {
    return Math.min(FORCE_LOGOUT_MAX_RETRY_DELAY_MS, FORCE_LOGOUT_FIRST_RETRY_DELAY_MS * 2 ** attempt);
  }

  function startWithRetry(attempt: number): void {
    hub.start().catch((error: unknown) => {
      if (disposed) {
        return;
      }

      reportError('Connecting to the logout hub failed.', error);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        startWithRetry(attempt + 1);
      }, retryDelayMs(attempt));
    });
  }

  return {
    start() {
      if (disposed || started) {
        return;
      }

      started = true;
      startWithRetry(0);
    },
    dispose() {
      disposed = true;
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      detachForceLogout();
      void hub.dispose();
    },
  };
}
